/**
 * The History card of an entity, filled from the case change log: the one place the pure card
 * under `components/entity-history` meets its reads — the events, and the summary its header
 * shows (PRD-0075 R-C1). An entity without a `_uid` has no history, and no card. With the case's
 * *Spis k dátumu* set, both read as of that day, under its banner (R-D4).
 */
import { useState } from 'react'
import {
  EntityHistoryCard,
  type EntityHistoryView,
} from '@/components/entity-history/EntityHistoryCard'
import { useCaseAsOf } from '@/features/case-as-of/CaseAsOf'
import { CaseAsOfBanner } from '@/features/case-as-of/CaseAsOfBanner'
import { HistoryMergeAudit } from './HistoryMergeAudit'
import { entityUid, toHistoryRows } from './history-rows'
import { toHeaderView } from './summary-view'
import { useEntityHistory } from './useEntityHistory'
import { useEntitySummary } from './useEntitySummaries'
import { useMemberNames } from './useMemberNames'

type OpenDocument = ((documentId: string) => void) | null

export interface EntityHistoryProps {
  caseId: string
  /** The node's own properties; its `_uid` names the history. */
  properties: Record<string, unknown>
  onOpenDocument: OpenDocument
}

/** The card of a selected graph node. */
export function EntityHistory({ caseId, properties, onOpenDocument }: EntityHistoryProps) {
  return (
    <EntityHistoryOf caseId={caseId} uid={entityUid(properties)} onOpenDocument={onOpenDocument} />
  )
}

export interface EntityHistoryOfProps {
  caseId: string
  /** The entity's stable id (ADR-0091); null renders nothing. */
  uid: string | null
  onOpenDocument: OpenDocument
}

/** The card of an entity known by its stable id (the identity page). */
export function EntityHistoryOf({ caseId, uid, onOpenDocument }: EntityHistoryOfProps) {
  const [audit, setAudit] = useState<{ uid: string | null; id: string } | null>(null)
  const auditId = audit?.uid === uid ? audit.id : null
  const setAuditId = (id: string | null) => setAudit(id ? { uid, id } : null)
  const { asOf } = useCaseAsOf()
  const query = useEntityHistory(caseId, uid, asOf)
  const summary = useEntitySummary(caseId, uid, asOf)
  const memberNames = useMemberNames(caseId)
  if (!uid) return null
  // Rows already read stay on screen when a refetch fails.
  const view: EntityHistoryView = query.data
    ? { status: 'ready', rows: toHistoryRows(query.data, { uid, memberNames }) }
    : query.isError
      ? { status: 'failed' }
      : { status: 'loading' }
  // As of a date, a change is recent against that date, not today.
  const now = asOf ? new Date(asOf) : new Date()
  return (
    <>
      <CaseAsOfBanner />
      <EntityHistoryCard
        view={view}
        summary={summary.data ? toHeaderView(summary.data, now) : null}
        actions={{ onOpenDocument, onOpenAudit: setAuditId }}
        onRetry={() => {
          void query.refetch()
          void summary.refetch()
        }}
      />
      <HistoryMergeAudit key={uid} caseId={caseId} auditId={auditId} onSelect={setAuditId} />
    </>
  )
}
